import { API_BASE_URL } from '../../utils/apiConfig';

/* Public pay-link API. No login: the token in the URL is the credential. */

export interface PayLine {
  name: string;
  quantity: number;
  price: number;
  taxableValue: number;
  gstPercent: number;
}

export interface PaySale {
  invoiceId: string | null;
  documentType: 'tax_invoice' | 'bill_of_supply';
  total: number;
  paid: number;
  due: number;
  taxes: { cgst: number; sgst: number; igst: number };
  lines: PayLine[];
}

export interface PayRequest {
  status: 'created' | 'sent' | 'paid' | 'expired' | 'cancelled';
  amount: number;
  paidAmount: number | null;
  currency: string;
  expiresAt: string;
  paidAt: string | null;
  gym: { name: string; logoUrl: string | null };
  payer: { firstName: string | null; phone: string | null };
  sales: PaySale[];
  /**
   * What is held while this is paid for; the page expires with the hold. Null = none.
   * `times` — a store cart's weekly times. `seat` — a drop-in booking's place,
   * released to the gym when the hold runs out.
   */
  hold:
    | { kind: 'times'; expiresAt: string; channel: 'app' | 'web' }
    | { kind: 'seat'; expiresAt: string }
    | null;
  /** The server's clock when it answered — countdowns run against it, not the device's. */
  now: string;
}

export type ClientCheckout =
  | { kind: 'sdk'; provider: string; payload: Record<string, any>; fallbackUrl?: string }
  | { kind: 'redirect'; url: string };

export class PayApiError extends Error {
  status: number;
  code?: string;
  constructor(status: number, body: any) {
    super(body?.error || `Request failed (${status})`);
    this.status = status;
    this.code = body?.code;
  }
}

async function call<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_BASE_URL}/api/pay${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    ...(body !== undefined && { body: JSON.stringify(body) }),
  });
  let json: any = null;
  try {
    json = await res.json();
  } catch {
    /* non-JSON */
  }
  if (!res.ok || json?.success === false) throw new PayApiError(res.status, json);
  return json.data as T;
}

export const getPayRequest = (token: string) => call<PayRequest>(`/${encodeURIComponent(token)}`);
export const startPayCheckout = (token: string) =>
  call<{ amount: number; checkout: ClientCheckout }>(`/${encodeURIComponent(token)}/checkout`, {});
export const verifyPayment = (token: string, result: Record<string, string>) =>
  call<{ status: string; outcome: string }>(`/${encodeURIComponent(token)}/verify`, result);
